// admin-panel/src/pages/CMS.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cmsApi } from '../lib/api';
import { Save, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

export default function CMS() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({});

  const { data, isLoading } = useQuery({ queryKey:['admin-cms'], queryFn: cmsApi.list });
  const list = data?.data || data || [];

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => cmsApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['admin-cms']); toast.success('Section saved!'); },
    onError: (e) => toast.error(e.message||'Save failed'),
  });

  function setEdit(id, k, v) {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [k]: v } }));
  }

  function getVal(item, k) {
    return edits[item.id]?.[k] ?? item[k] ?? '';
  }

  const INPUT = "w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-white">CMS / Page Content</h1>
        <p className="text-sm text-[#555] mt-0.5">Edit homepage sections, services content, and other page content</p>
      </div>

      {isLoading ? (
        Array(5).fill(0).map((_,i) => <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse"/>)
      ) : list.length === 0 ? (
        <div className="text-center py-16 text-[#555]"><BookOpen size={40} className="mx-auto mb-3 text-[#333]"/><p>No CMS sections found. They will appear when created.</p></div>
      ) : (
        <div className="space-y-3">
          {list.map(item => (
            <div key={item.id} className="bg-[#151515] border border-white/5 rounded-xl overflow-hidden">
              <button className="w-full flex items-center justify-between px-5 py-4" onClick={()=>setExpanded(expanded===item.id?null:item.id)}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]"/>
                  <div className="text-left">
                    <div className="text-sm font-medium text-white capitalize">{item.section_key?.replace(/_/g,' ')} <span className="text-[#555] text-xs">({item.page})</span></div>
                    {item.title && <div className="text-xs text-[#555] mt-0.5">{item.title}</div>}
                  </div>
                </div>
                {expanded===item.id ? <ChevronUp size={16} className="text-[#888]"/> : <ChevronDown size={16} className="text-[#888]"/>}
              </button>

              {expanded===item.id && (
                <div className="px-5 pb-5 border-t border-white/5 pt-4 space-y-3">
                  {[{k:'title',l:'Title',p:'Section title'},{k:'subtitle',l:'Subtitle',p:'Section subtitle'}].map(({k,l,p})=>(
                    <div key={k}>
                      <label className="block text-xs text-[#555] mb-1.5">{l}</label>
                      <input value={getVal(item,k)} onChange={e=>setEdit(item.id,k,e.target.value)} placeholder={p} className={INPUT}/>
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs text-[#555] mb-1.5">Content</label>
                    <textarea value={getVal(item,'content')} onChange={e=>setEdit(item.id,'content',e.target.value)} rows={5} placeholder="Section content…" className={`${INPUT} resize-none`}/>
                  </div>
                  <button onClick={()=>updateMut.mutate({id:item.id,data:{...item,...edits[item.id]}})} disabled={updateMut.isPending}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50">
                    <Save size={14}/> Save Section
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
